import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { cn } from './utils';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  variant?: ToastVariant;
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-success-bg border border-success-border text-success-fg',
  error: 'bg-error-bg border border-error-border text-error-fg',
  info: 'bg-info-bg border border-info-border text-info-fg',
  warning: 'bg-warning-bg border border-warning-border text-warning-fg',
};

const variantIcons: Record<ToastVariant, IconName> = {
  success: 'Check',
  error: 'X',
  info: 'Info',
  warning: 'TriangleAlert',
};

const baseStyles =
  'px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-[400px]';

export function Toast({ variant = 'info', children, onDismiss, className = '' }: ToastProps) {
  const cls = cn(baseStyles, variantStyles[variant], className);
  return (
    <div className={cls} role="status" aria-live="polite">
      <Icon name={variantIcons[variant]} size="sm" className="flex-shrink-0" />
      <span className="flex-1 text-sm">{children}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-neutral-400 hover:text-neutral-600 flex-shrink-0 transition-colors"
          aria-label="Dismiss"
        >
          <Icon name="X" size="sm" />
        </button>
      )}
    </div>
  );
}

export interface ToastViewportProps {
  children: ReactNode;
  className?: string;
}

/** Fixed-positioned stack container for rendering active toasts. */
export function ToastViewport({ children, className = '' }: ToastViewportProps) {
  const cls = cn('fixed bottom-4 right-4 z-50 flex flex-col gap-2', className);
  return <div className={cls}>{children}</div>;
}
